































































































































import { Component, Vue, Watch } from "vue-property-decorator";
import {
  AddressSuggestion,
  CrossCheckVerdict,
  GeoLocation,
  GeoLocationConfidence,
  GeoLocationQuality,
  GeocodeOutcome,
  LocationPickerResult,
  PickedAddressParts,
  PickerAddress,
  geocodeAddress,
  geocodeByPlaceId,
  getConfidenceBandColors,
  getLocationConfidenceColors,
  getLocationConfidenceKey,
  getLocationMapZoom,
  hasPickerAddressChanged,
  resolveCountryFilter,
  reverseGeocode,
  trimSuggestionDescription,
} from "@/utils/GeocodingService";
import { createPlacesLookup } from "@/data/repository/GeocodingRepository";
import { loadGoogleMaps } from "@/utils/GoogleMapsLoader";
import { i18n } from "@/plugins/vuetify";
import { themed, themedFill, themedFillTextColor } from "@/utils/Theme";

/* global google */


/**
 * Zoom used for a position the user has just chosen or placed themselves.
 *
 * The zoom for a *stored* position follows how precisely it is known instead: see
 * {@link getLocationMapZoom}.
 */
const ZOOM_CHOSEN = 18;

/** Zoom used when nothing could be resolved, showing roughly a country. */
const ZOOM_UNLOCATED = 5;

/** Fallback centre when there is no address and no stored pin: roughly central Germany. */
const FALLBACK_CENTER = { lat: 51.1657, lng: 10.4515 };

/** How long to wait after the pin stops moving before asking Google what is there. */
const REVERSE_GEOCODE_DEBOUNCE_MS = 400;

/** How long to wait after typing stops before asking Places for suggestions. */
const SUGGESTION_DEBOUNCE_MS = 300;

/** Below this, a query matches half the country and the suggestions are noise. */
const MIN_SUGGESTION_INPUT_LENGTH = 3;

/**
 * The mdi-map-marker outline, so the pin matches the icon the host surfaces show. The inner
 * circle is wound the other way, which cuts it out of the fill rather than painting over it.
 */
const MARKER_PATH =
  "M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z";

/**
 * Map dialog for confirming or fine-tuning the position of an address.
 *
 * Opens on the stored position, or geocodes the address when there is none, or when the host says the
 * address has been edited since that position was stored. The user drags the pin, clicks the map, or
 * picks an address from the suggestion list, and the address currently under the pin is shown so they
 * can tell they are on the right building. The pin is coloured by how much the position can be
 * trusted.
 *
 * Applying a moved pin yields quality `MANUAL`. Since 2026-08-18 that no longer means "never
 * overwritten": an edited address replaces even a hand-placed pin, here and in IRES Cloud, because a
 * pin describes the address it was placed for. A pin that was never moved keeps whatever quality
 * Google reported.
 *
 * A pin the user placed or picked also carries the address under it, so a host can fill an empty
 * address form from the map (ticket 2610025). A pin only confirmed where the typed address put it
 * carries none.
 *
 * Usage: `const result = await picker.show(address, coordinates, readonly, storedPositionAddress)`.
 * Resolves to the chosen {@link LocationPickerResult}, or `null` when the user cancelled.
 */
@Component
export default class LocationPickerDialogComponent extends Vue {
  isShowing = false;
  readonly = false;

  /** True while the map script, the initial geocode or a reverse lookup is in flight. */
  isBusy = false;

  /** i18n key of a failure worth telling the user about, if any. */
  errorKey: string | null = null;

  /** Formatted address under the pin, from reverse geocoding. */
  pinAddress = "";

  /** The address under the pin split into form fields, once it is known. */
  pinAddressParts: PickedAddressParts | null = null;

  /** Whether the user moved the pin during this session. */
  hasMovedPin = false;

  /** True when the geocoded position resolves back to a different address than the input. */
  hasCrossCheckMismatch = false;

  /**
   * True when Google offered several different buildings for the address and one was picked.
   *
   * Named rather than hidden, because the pin looks exactly as confident either way. The user
   * is the only one who can say which reading was meant, so the alternatives are listed and
   * the choice is handed over instead of being made for them.
   */
  isAmbiguous = false;

  /**
   * How many readings Google offered, for wording the warning.
   *
   * The readings themselves go into {@link suggestions}, so the user resolves the address by
   * picking one instead of reading a list and retyping.
   */
  ambiguousCount = 0;

  /** Whether Places loaded; the search field is pointless without it. */
  isSearchAvailable = false;

  isSearching = false;
  searchInput: string | null = null;
  selectedSuggestion: string | null = null;
  suggestions: AddressSuggestion[] = [];

  private address: PickerAddress = {};

  /**
   * Whether the stored pin was placed for an address that has since been edited.
   *
   * Such a pin marks the previous building, so opening on it invites confirming a position for an
   * address nobody entered. IRES Cloud replaces the stored position on the same grounds when the save
   * reaches it (decision of 2026-08-18).
   */
  private isStoredPositionStale = false;
  private position: GeoLocation | null = null;
  private resolvePromise: ((result: LocationPickerResult | null) => void) | null = null;

  private map: google.maps.Map | null = null;
  private marker: google.maps.Marker | null = null;
  private reverseGeocodeTimer: number | null = null;
  /** The point waiting out the reverse-lookup debounce, if any. */
  private reverseGeocodePoint: { lat: number; lng: number } | null = null;
  /** The reverse lookup on its way back, so applying can wait for it. */
  private reverseGeocodeInFlight: Promise<void> | null = null;
  /** Bumped whenever the pin moves, so an answer for where it used to be is discarded. */
  private reverseGeocodeRevision = 0;
  private suggestionTimer: number | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private autocompleteService: any = null;

  get displayTitle(): string {
    return this.$t("location.pickerTitle").toString();
  }

  get hasPin(): boolean {
    return this.position !== null;
  }

  get confidenceKey(): string {
    return getLocationConfidenceKey(this.position);
  }

  /**
   * The confidence chip's fill: the dark palette shade of its band in dark mode. A band is a
   * status, so an unreliable position takes the status red. (The pin and icons keep their own
   * dark-mode colours, which are indicators rather than a label's background.)
   */
  get confidenceColor(): string {
    return themedFill(getLocationConfidenceColors(this.position).light, "status");
  }

  /** Dark text on the chip's palette fill in dark mode; light mode keeps the chip's white (`dark`). */
  get confidenceTextColor(): string {
    return themedFillTextColor(getLocationConfidenceColors(this.position).light, "", "status");
  }

  /**
   * The four confidence bands with their colours, best first, for the legend.
   *
   * Built from {@link GeoLocationConfidence} and the same colour helper the pin and the chip
   * use, so the legend cannot drift from what it explains. Ordered deliberately rather than by
   * enum declaration: a legend that reads green to red is a scale, one in arbitrary order is a
   * list of colours.
   */
  get confidenceLegend(): { key: string; color: string }[] {
    const bands = [
      GeoLocationConfidence.CONFIRMED,
      GeoLocationConfidence.GOOD,
      GeoLocationConfidence.POOR,
      GeoLocationConfidence.UNRELIABLE,
    ];

    return bands.map((band) => {
      const colors = getConfidenceBandColors(band);

      return {
        key: `location.legend.${band}`,
        color: themed(colors.light, colors.dark),
      };
    });
  }

  /**
   * Opens the picker.
   *
   * @param address - the address as currently entered, used to centre the map when there
   *   is no stored position.
   * @param coordinates - the stored position, if any.
   * @param readonly - open for viewing only, matching a host surface in view mode.
   * @param storedPositionAddress - the address as it was saved, when the host knows it. Given that,
   *   the picker can tell that the address has been edited since the stored pin was placed for it,
   *   and opens on the edited address instead of on a pin that now marks a different building. Omit
   *   it and the stored pin is always shown, which is the old behaviour.
   * @returns the chosen position with the address under it, or null if the user cancelled or
   *   closed the dialog.
   */
  show(
    address: PickerAddress,
    coordinates: GeoLocation | null | undefined,
    readonly = false,
    storedPositionAddress?: PickerAddress
  ): Promise<LocationPickerResult | null> {
    this.address = address;
    this.position = coordinates ?? null;
    // Only ever true when there is something to reposition: with no stored pin the map geocodes the
    // address anyway.
    this.isStoredPositionStale = !!this.position
      && !!storedPositionAddress
      && hasPickerAddressChanged(storedPositionAddress, address);
    this.readonly = readonly;
    this.hasMovedPin = false;
    this.hasCrossCheckMismatch = false;
    this.isAmbiguous = false;
    this.ambiguousCount = 0;
    this.pinAddress = "";
    this.pinAddressParts = null;
    this.reverseGeocodePoint = null;
    this.reverseGeocodeInFlight = null;
    this.errorKey = null;
    this.isSearchAvailable = false;
    this.isSearching = false;
    this.searchInput = null;
    this.selectedSuggestion = null;
    this.suggestions = [];
    this.isShowing = true;

    // Deliberately after isShowing, so the dialog and its spinner are on screen while the
    // map script loads rather than the button appearing to do nothing.
    this.$nextTick(() => this.initializeMap());

    return new Promise<LocationPickerResult | null>(resolve => {
      this.resolvePromise = resolve;
    });
  }

  private async initializeMap() {
    this.isBusy = true;

    try {
      // `places` powers the suggestion list. The loader honours `libraries` only on the
      // first call for the page lifetime, so this must match what MapsPage asks for.
      await loadGoogleMaps(["places"]);
    } catch (error) {
      // The map is the whole dialog; without it there is nothing to show.
      this.errorKey = "location.mapLoadFailed";
      this.isBusy = false;
      return;
    }

    this.initializeAutocomplete();

    if (!this.position || this.isStoredPositionStale) {
      // The stored pin stays as the fallback for a lookup that fails: it is what is actually on the
      // record, and an unpositioned map is a worse starting point than a pin the user can drag. The
      // failure is on screen either way, from `geocodeCurrentAddress`.
      this.position = (await this.geocodeCurrentAddress()) ?? this.position;
    }

    const canvas = this.$refs.mapCanvas as HTMLElement | undefined;

    // The user can close the dialog while the script is loading, which unmounts the
    // canvas and leaves google.maps.Map nothing to attach to.
    if (!canvas) {
      this.isBusy = false;
      return;
    }

    const center = this.position
      ? { lat: this.position.latitude, lng: this.position.longitude }
      : FALLBACK_CENTER;

    this.map = new google.maps.Map(canvas, {
      center,
      // Not a flat building zoom: a locality-grade position opened at zoom 18 renders as an
      // empty green rectangle, which is how a Karlsruhe address stored in Italy came to look
      // like nothing at all rather than like the wrong country.
      zoom: this.position ? getLocationMapZoom(this.position) : ZOOM_UNLOCATED,
      streetViewControl: false,
      // The roadmap draws no building footprints at some zooms, which reads as "the house
      // is not even there". Letting the user switch to satellite settles it in one click.
      mapTypeControl: true,
      fullscreenControl: false,
      gestureHandling: "greedy",
    });

    if (this.position) {
      this.placeMarker(center, false);
    }

    if (!this.readonly) {
      this.map.addListener("click", (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
          this.placeMarker(
            { lat: event.latLng.lat(), lng: event.latLng.lng() },
            true
          );
        }
      });
    }

    this.isBusy = false;
  }

  /** Wires up the Places suggestion service, silently doing without it if it is absent. */
  private initializeAutocomplete() {
    if (this.readonly) {
      return;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const places = (google.maps as any).places;

      if (places?.AutocompleteService) {
        this.autocompleteService = new places.AutocompleteService();
        this.isSearchAvailable = true;
      }
    } catch (error) {
      // Places is an optional convenience; the picker's job is done by the map alone.
      this.isSearchAvailable = false;
    }
  }

  /** Geocodes the address the host surface passed in. */
  private async geocodeCurrentAddress(): Promise<GeoLocation | null> {
    const { street, houseNumber, city, zipCode } = this.address;

    if (!street && !houseNumber && !city && !zipCode) {
      this.errorKey = "location.noAddress";
      return null;
    }

    // Places, via IRES Cloud, is what finds the addresses the Geocoding API alone does not,
    // such as `Wachthaus 2, Lorch`.
    const result = await geocodeAddress(this.address, {
      language: i18n.locale,
      places: createPlacesLookup(i18n.locale),
    });

    if (result.outcome !== GeocodeOutcome.OK || !result.coordinates) {
      // Not fatal: the user can still place a pin by hand, which is precisely what the
      // picker is for when an address will not resolve.
      this.errorKey = "location.geocodingFailed";
      return null;
    }

    this.hasCrossCheckMismatch = result.crossCheck === CrossCheckVerdict.MISMATCH;
    this.isAmbiguous = result.isAmbiguous;
    this.ambiguousCount = result.alternatives.length;

    if (result.isAmbiguous) {
      // Offer the readings straight away: picking one resolves the address exactly and counts
      // as the user confirming it, which is the only way this address can reach green.
      this.suggestions = result.alternatives;
    }

    return result.coordinates;
  }

  @Watch("searchInput")
  onSearchInputChanged(input: string | null) {
    if (this.suggestionTimer !== null) {
      window.clearTimeout(this.suggestionTimer);
      this.suggestionTimer = null;
    }

    if (!this.autocompleteService || !input || input.length < MIN_SUGGESTION_INPUT_LENGTH) {
      this.suggestions = [];
      this.isSearching = false;
      return;
    }

    // Selecting an entry makes Vuetify write its text back into the search field, which would
    // otherwise spend a billed Places request re-asking about the answer we just accepted.
    if (this.suggestions.some(suggestion => suggestion.description === input)) {
      this.isSearching = false;
      return;
    }

    this.isSearching = true;
    this.suggestionTimer = window.setTimeout(
      () => this.requestSuggestions(input),
      SUGGESTION_DEBOUNCE_MS
    );
  }

  /** Asks Places what addresses match what has been typed so far. */
  private requestSuggestions(input: string) {
    const isoCountry = resolveCountryFilter([
      this.address.country,
      ...(this.address.countryFallbacks ?? []),
    ]);

    this.autocompleteService.getPlacePredictions(
      {
        input,
        types: ["address"],
        language: i18n.locale,
        ...(isoCountry && { componentRestrictions: { country: isoCountry } }),
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (predictions: any[] | null) => {
        if (!this.isShowing) {
          return;
        }

        this.isSearching = false;
        this.suggestions = (predictions ?? []).map(prediction => ({
          placeId: prediction.place_id,
          // Country dropped, district kept: it is what tells four same-named streets apart.
          description: trimSuggestionDescription(prediction.description),
        }));
      }
    );
  }

  /**
   * Moves the pin onto an address the user chose from the list.
   *
   * Choosing from the list is an act of confirmation in its own right, so the position is
   * stored as hand-placed rather than keeping whatever class Google attaches to it.
   */
  async onSuggestionPicked(placeId: string | null) {
    if (!placeId) {
      return;
    }

    this.isBusy = true;

    const result = await geocodeByPlaceId(placeId, i18n.locale);

    this.isBusy = false;

    if (!result.coordinates) {
      this.errorKey = "location.geocodingFailed";
      return;
    }

    this.errorKey = null;
    this.hasCrossCheckMismatch = false;
    this.hasMovedPin = true;

    const latLng = {
      lat: result.coordinates.latitude,
      lng: result.coordinates.longitude,
    };

    this.map?.setCenter(latLng);
    this.map?.setZoom(ZOOM_CHOSEN);
    this.placeMarker(latLng, true);

    // Google's own rendering of the chosen address beats waiting for the reverse lookup. It also
    // wins over it: the reverse lookup of these coordinates can land on the neighbouring house,
    // and the place picked from the list is the address the user meant.
    if (result.formattedAddress) {
      this.cancelReverseGeocode();
      this.pinAddress = result.formattedAddress;
      this.pinAddressParts = result.addressParts ?? null;
    }
  }

  /**
   * Draws or moves the pin.
   *
   * @param latLng - where to put it.
   * @param movedByUser - true when this came from a drag, a map click or a picked
   *   suggestion, which is what marks the position as hand-placed.
   */
  private placeMarker(
    latLng: { lat: number; lng: number },
    movedByUser: boolean
  ) {
    if (!this.map) {
      return;
    }

    if (movedByUser) {
      this.hasMovedPin = true;
      this.errorKey = null;
      // The user overrode the automatic result, so its disagreement no longer applies.
      this.hasCrossCheckMismatch = false;
    }

    this.position = {
      latitude: latLng.lat,
      longitude: latLng.lng,
      // A pin the user placed is MANUAL from now on. One that merely reflects the
      // geocoder keeps the quality Google gave it.
      ...(movedByUser
        ? {
            quality: GeoLocationQuality.MANUAL,
            confidence: GeoLocationConfidence.CONFIRMED,
          }
        : {
            ...(this.position?.quality && { quality: this.position.quality }),
            ...(this.position?.confidence && {
              confidence: this.position.confidence,
            }),
          }),
    };

    if (!this.marker) {
      this.marker = new google.maps.Marker({
        position: latLng,
        map: this.map,
        draggable: !this.readonly,
        icon: this.markerIcon(),
      });

      this.marker.addListener("dragend", () => {
        const dragged = this.marker?.getPosition();
        if (dragged) {
          this.placeMarker({ lat: dragged.lat(), lng: dragged.lng() }, true);
        }
      });
    } else {
      this.marker.setPosition(latLng);
      this.marker.setIcon(this.markerIcon());
    }

    this.scheduleReverseGeocode(latLng);
  }

  /** The pin symbol, tinted by the current position's confidence band. */
  private markerIcon(): google.maps.Symbol {
    return {
      path: MARKER_PATH,
      fillColor: this.confidenceColor,
      fillOpacity: 1,
      strokeColor: "#ffffff",
      strokeWeight: 1,
      scale: 1.8,
      // The tip of the teardrop, so the pin points at the coordinate rather than hovering
      // with its centre on it.
      anchor: new google.maps.Point(12, 22),
    };
  }

  /**
   * Looks up what is at the pin, after it has been still for a moment.
   *
   * Debounced because a drag emits a position continuously; without it every intermediate
   * pixel would cost a billed request.
   */
  private scheduleReverseGeocode(latLng: { lat: number; lng: number }) {
    this.cancelReverseGeocode();
    this.pinAddressParts = null;
    this.reverseGeocodePoint = latLng;
    this.reverseGeocodeTimer = window.setTimeout(
      () => this.runReverseGeocode(),
      REVERSE_GEOCODE_DEBOUNCE_MS
    );
  }

  /**
   * Runs the waiting reverse lookup now, or returns the one already on its way.
   *
   * Resolves once the readout and {@link pinAddressParts} describe the pin, so applying can wait
   * for it instead of handing back a pin without its address.
   */
  private runReverseGeocode(): Promise<void> {
    const point = this.reverseGeocodePoint;

    if (this.reverseGeocodeTimer !== null) {
      window.clearTimeout(this.reverseGeocodeTimer);
      this.reverseGeocodeTimer = null;
    }

    this.reverseGeocodePoint = null;

    if (!point) {
      return this.reverseGeocodeInFlight ?? Promise.resolve();
    }

    const revision = this.reverseGeocodeRevision;

    this.reverseGeocodeInFlight = reverseGeocode(point.lat, point.lng, i18n.locale)
      .then((address) => {
        // The dialog may have closed, or the pin moved on, while the lookup was in flight.
        if (this.isShowing && revision === this.reverseGeocodeRevision) {
          this.pinAddress = address?.line ?? "";
          this.pinAddressParts = address?.parts ?? null;
        }
      });

    return this.reverseGeocodeInFlight;
  }

  /** Drops a waiting reverse lookup, and makes one already in flight answer into nothing. */
  private cancelReverseGeocode() {
    if (this.reverseGeocodeTimer !== null) {
      window.clearTimeout(this.reverseGeocodeTimer);
      this.reverseGeocodeTimer = null;
    }

    this.reverseGeocodePoint = null;
    this.reverseGeocodeRevision++;
  }

  /**
   * Hands back the pin, with the address under it when the user placed or picked it.
   *
   * The address is looked up a moment after the pin stops moving, so a quick apply waits for that
   * lookup rather than returning the pin without it.
   */
  async onApply() {
    if (!this.position) {
      return;
    }

    if (this.hasMovedPin) {
      this.isBusy = true;
      await this.runReverseGeocode();
      this.isBusy = false;
    }

    // Cancelled while the lookup was still running.
    if (!this.resolvePromise || !this.position) {
      return;
    }

    this.close({
      position: this.position,
      address: this.hasMovedPin ? this.pinAddressParts : null,
    });
  }

  onCancel() {
    this.close(null);
  }

  /** A click outside closes only in readonly mode, where nothing can be lost. */
  onOutsideClick() {
    if (this.readonly) {
      this.close(null);
    }
  }

  private close(result: LocationPickerResult | null) {
    this.clearTimers();

    this.isShowing = false;
    this.map = null;
    this.marker = null;
    this.autocompleteService = null;

    this.resolvePromise?.(result);
    this.resolvePromise = null;
  }

  private clearTimers() {
    this.cancelReverseGeocode();

    if (this.suggestionTimer !== null) {
      window.clearTimeout(this.suggestionTimer);
      this.suggestionTimer = null;
    }
  }

  beforeDestroy() {
    this.clearTimers();
  }
}
